import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import AgentTenderForm from "../../components/agent-tender/AgentTenderForm";
import { AgentTenderPageHeader } from "./CreateAgentTender";
import { agentTenderService } from "../../services/agentTenderApi";
import { VERIFICATION_STATUS } from "../../constants/agentTenderChoices";

// Map an internal "mine"/detail record back onto the form's field shape.
//
// Root cause of the empty-edit bug: the old mapper carried over only 8 of the
// ~30 stored fields, so everything else fell back to the form's EMPTY defaults
// — the form opened blank and a save silently wiped the unmapped values. This
// maps EVERY field the API returns, bridging the API names onto the form names:
//   net_salary_from / gross_salary_from / fix_net_salary → salary_from/salary_fixed
//   fix_market_value → market_value_fixed
//   trial_period_start_date / _end_date → trial_start / trial_end
//   commission_split "60 / 40" → commission_posting / commission_responding
//   club {id,name,country,logo} → ClubPicker shape (platform_data.club_id, so
//     buildPayload re-sends the same club_id and the club stays editable)
// Keys are only set when the API provides a value, so {...EMPTY, ...initial}
// keeps the form defaults for anything absent, and the form itself derives the
// games/age/height "Specify vs Not important" toggles from the values.
const has = (v) => v !== null && v !== undefined && v !== "";
const str = (v) => (has(v) ? String(v) : "");

const toFormShape = (t) => {
  if (!t) return null;
  const [posting = "", responding = ""] = (t.commission_split || "")
    .split("/")
    .map((s) => s.trim());

  // Salary: the API stores net OR gross columns; the form has one from/to/fixed
  // trio plus a net/gross dropdown — infer the calculation from which side is set.
  const isGross =
    has(t.gross_salary_from) || has(t.gross_salary_to) || has(t.fix_gross_salary);
  const salaryFrom = isGross ? t.gross_salary_from : t.net_salary_from;
  const salaryTo = isGross ? t.gross_salary_to : t.net_salary_to;
  const salaryFixed = isGross ? t.fix_gross_salary : t.fix_net_salary;

  // has_loan_fee is not (yet) part of the mine payload — infer it from a
  // positive stored loan fee when the flag itself is absent.
  const hasLoanFee =
    t.has_loan_fee != null ? !!t.has_loan_fee : Number(t.loan_fee_amount) > 0;

  return {
    club: t.club
      ? {
          name: t.club.name || "",
          country: t.club.country || "",
          logo: t.club.logo || "",
          // A stored tender's club is on-platform by definition (it is the
          // tender's FK) — shape it the way ClubPicker/buildPayload expect so
          // the same club_id is re-sent and "Change" still works.
          platform_data: { exists: true, club_id: t.club.id },
        }
      : null,
    tender_category: t.tender_category || "player",
    position: t.position || "",
    staff_role: t.staff_role || "",
    type_of_tender: t.type_of_tender || "buy",
    recruiting_for: t.recruiting_for || "professional",
    ...(has(t.currency) && { currency: t.currency }),
    ...(isGross && { salary_calculation: "gross" }),
    ...(has(t.salary_type) && { salary_type: t.salary_type }),
    ...(has(salaryFrom) && { salary_from: str(salaryFrom) }),
    ...(has(salaryTo) && { salary_to: str(salaryTo) }),
    ...(has(salaryFixed) && { salary_fixed: str(salaryFixed) }),
    ...(has(t.market_value_type) && { market_value_type: t.market_value_type }),
    ...(has(t.market_value_from) && { market_value_from: str(t.market_value_from) }),
    ...(has(t.market_value_to) && { market_value_to: str(t.market_value_to) }),
    ...(has(t.fix_market_value) && { market_value_fixed: str(t.fix_market_value) }),
    ...(has(t.min_age) && { min_age: str(t.min_age) }),
    ...(has(t.max_age) && { max_age: str(t.max_age) }),
    ...(has(t.minimum_height) && { minimum_height: str(t.minimum_height) }),
    ...(has(t.foot) && { foot: t.foot }),
    ...(has(t.experience) && { experience: t.experience }),
    only_free_players: !!t.only_free_players,
    only_eu_player: !!t.only_eu_player,
    ...(has(t.maximum_transfer_fee) && {
      maximum_transfer_fee: str(t.maximum_transfer_fee),
    }),
    ...(t.loan_with_option_to_buy != null && {
      loan_with_option_to_buy: !!t.loan_with_option_to_buy,
    }),
    ...((t.has_loan_fee != null || has(t.loan_fee_amount)) && {
      has_loan_fee: hasLoanFee,
    }),
    ...(has(t.loan_fee_amount) && { loan_fee_amount: str(t.loan_fee_amount) }),
    ...(has(t.trial_period_start_date) && { trial_start: t.trial_period_start_date }),
    ...(has(t.trial_period_end_date) && { trial_end: t.trial_period_end_date }),
    ...(has(t.matches_played_last_season) && {
      matches_played_last_season: str(t.matches_played_last_season),
    }),
    ...(has(t.matches_played_last_half_season) && {
      matches_played_last_half_season: str(t.matches_played_last_half_season),
    }),
    ...(has(t.tolerance_value) && { tolerance_value: str(t.tolerance_value) }),
    description: t.description || "",
    key_requirements: t.key_requirements || "",
    commission_posting: posting,
    commission_responding: responding,
    commission_note: t.commission_split_note || "",
    // ── V2 fields (agent-request V2 build, 2026-07-21) ──
    ...(has(t.target_window) && { target_window: t.target_window }),
    ...(has(t.staff_start_type) && { staff_start_type: t.staff_start_type }),
    ...(has(t.staff_start_month) && { staff_start_month: t.staff_start_month }),
    // Legacy club-tender rows default temporary_duration to 0 = "not set";
    // only a positive stored duration prefills the field.
    ...(Number(t.temporary_duration) > 0 && {
      temporary_duration: str(t.temporary_duration),
    }),
    ...(has(t.temporary_duration_unit) && {
      temporary_duration_unit: t.temporary_duration_unit,
    }),
    ...(has(t.experience_min_years) && {
      experience_min_years: str(t.experience_min_years),
    }),
    experience_not_important: !!t.experience_not_important,
    no_commission: !!t.no_commission,
    ...(has(t.other_staff_position) && {
      other_staff_position: t.other_staff_position,
    }),
  };
};

const EditAgentTender = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // The feed/detail endpoint returns the agent's own record in full when
      // they own it (Rule 8). For the mock layer we read from the mine list.
      const res = await agentTenderService.getFeedOne(id);
      if (cancelled) return;
      if (res.success) setTender(res.data);
      else setError(res.error || "Could not load this request.");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSubmit = async (payload) => {
    const res = await agentTenderService.update(id, payload);
    if (res.success) {
      toast.success("Request updated.");
      navigate("/agent-tenders/mine");
    }
    return res;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Wrap widths per artboard (same as create): full-width to 1024 (form
          centered at 760), rail joins ≥1280, wrap capped at 1280px from 1920. */}
      <div className="px-4 md:px-6 lg:px-7 xl:px-8 py-5">
        <div className="mx-auto w-full lg:max-w-[760px] xl:max-w-none min-[1920px]:max-w-[1280px]">
        <AgentTenderPageHeader
          title="Edit request"
          onBack={() => navigate("/agent-tenders/mine")}
          backLabel="Back to my requests"
          onCancel={() => navigate("/agent-tenders/mine")}
        />

        {loading ? (
          <div className="text-gray-500 py-10">Loading…</div>
        ) : error ? (
          <div className="text-red-500 py-10">{error}</div>
        ) : (
          <AgentTenderForm
            initial={toFormShape(tender)}
            isVerified={
              tender?.verification_status === VERIFICATION_STATUS.CLUB_VERIFIED
            }
            submitLabel="Save changes"
            onSubmit={handleSubmit}
            onCancel={() => navigate("/agent-tenders/mine")}
          />
        )}
        </div>
      </div>
    </div>
  );
};

export default EditAgentTender;
